import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import type { Category } from "@/hooks/useCategories";
import type { ArtistDetail } from "@/hooks/useArtistDetails";
import type { Product, CreateProductData, UpdateProductData } from "@/hooks/useProducts";
import { ProductSizeManager } from "./ProductSizeManager";
import { useProductSizes } from "@/hooks/useProductSizes";

interface ProductFormProps {
  categories: Category[];
  artistDetails: ArtistDetail[];
  editingProduct: Product | null;
  onCreateProduct: (data: CreateProductData) => Promise<Product | null>;
  onUpdateProduct: (data: UpdateProductData) => Promise<Product | null>;
  onClose: () => void;
}

export function ProductForm({
  categories,
  artistDetails,
  editingProduct,
  onCreateProduct,
  onUpdateProduct,
  onClose,
}: ProductFormProps) {

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    original_price: "",
    category: "",
    categorySlug: "",
    tags: [] as string[],
    in_stock: true,
    main_image: "",
    images: [] as string[],
    rating: "0",
    reviews_count: "0",
    artistid: "no-artist",
  });

  const [imageInputs, setImageInputs] = useState<string[]>([""]);
  const [imageInputValues, setImageInputValues] = useState<string[]>([""]);
  const [createdProductId, setCreatedProductId] = useState<string | null>(null);
  
  const { toast } = useToast();
  
  // Use product sizes hook - only fetch when we have a product ID
  const {
    sizes,
    loading: sizesLoading,
    createSize,
    updateSize,
    deleteSize,
    reorderSizes,
  } = useProductSizes(createdProductId || editingProduct?.id);

  useEffect(() => {
    if (editingProduct) {
      const categoryObj = categories.find(cat => cat.slug === editingProduct.category);
      const categoryName = categoryObj?.name || editingProduct.category;
      
      setFormData({
        name: editingProduct.name,
        description: editingProduct.description || "",
        price: editingProduct.price.toString(),
        original_price: editingProduct.original_price?.toString() || "",
        category: categoryName,
        categorySlug: editingProduct.category,
        tags: editingProduct.tags || [],
        in_stock: editingProduct.in_stock,
        main_image: editingProduct.main_image || "",
        images: editingProduct.images || [],
        rating: editingProduct.rating.toString(),
        reviews_count: editingProduct.reviews_count.toString(),
        artistid: editingProduct.artistid || "no-artist",
      });

      const productImages = editingProduct.images || [];
      if (productImages.length > 0) {
        setImageInputs(Array(productImages.length).fill(""));
        setImageInputValues([...productImages]);
      } else {
        setImageInputs([""]);
        setImageInputValues([""]);
      }
    } else {
      resetForm();
    }
  }, [editingProduct, categories]);

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      price: "",
      original_price: "",
      category: "",
      categorySlug: "",
      tags: [],
      in_stock: true,
      main_image: "",
      images: [],
      rating: "0",
      reviews_count: "0",
      artistid: "no-artist",
    });
    setImageInputs([""]);
    setImageInputValues([""]);
  };

  const addImageInput = () => {
    if (imageInputs.length < 4) {
      setImageInputs([...imageInputs, ""]);
      setImageInputValues([...imageInputValues, ""]);
    }
  };

  const removeImageInput = (index: number) => {
    if (imageInputs.length > 1) {
      const newInputs = imageInputs.filter((_, i) => i !== index);
      const newValues = imageInputValues.filter((_, i) => i !== index);
      setImageInputs(newInputs);
      setImageInputValues(newValues);
      
      const validImages = newValues.filter(img => img.trim() !== "");
      setFormData({ ...formData, images: validImages });
    }
  };

  const handleImageInputChange = (index: number, value: string) => {
    const newValues = [...imageInputValues];
    newValues[index] = value;
    setImageInputValues(newValues);
    
    const validImages = newValues.filter(img => img.trim() !== "");
    setFormData({ ...formData, images: validImages });
  };

  const handleCategoryChange = (categoryName: string) => {
    const selectedCategory = categories.find(cat => cat.name === categoryName);
    setFormData({ 
      ...formData, 
      category: categoryName,
      categorySlug: selectedCategory?.slug || categoryName
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (!formData.name.trim()) {
        throw new Error("Product name is required");
      }
      
      if (!formData.price || parseFloat(formData.price) <= 0) {
        throw new Error("Valid price is required");
      }
      
      if (!formData.categorySlug) {
        throw new Error("Category is required");
      }
      
      const productData: CreateProductData | UpdateProductData = {
        name: formData.name.trim(),
        description: formData.description?.trim() || undefined,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : undefined,
        category: formData.categorySlug,
        tags: formData.tags.filter(tag => tag.trim() !== ''),
        in_stock: formData.in_stock,
        main_image: formData.main_image?.trim() || undefined,
        images: formData.images.filter(img => img.trim() !== ''),
        rating: parseFloat(formData.rating) || 0,
        reviews_count: parseInt(formData.reviews_count) || 0,
        artistid: formData.artistid === "no-artist" ? null : (formData.artistid?.trim() || undefined),
      };

      if (editingProduct) {
        const updatedProduct = await onUpdateProduct({
          id: editingProduct.id,
          ...productData,
        });
        
        if (updatedProduct) {
          toast({
            title: "Success",
            description: "Product updated successfully",
          });
          onClose();
          resetForm();
        } else {
          throw new Error("Failed to update product");
        }
      } else {
        const newProduct = await onCreateProduct(productData as CreateProductData);
        
        if (newProduct) {
          toast({
            title: "Success",
            description: "Product created successfully. You can now add sizes.",
          });
          setCreatedProductId(newProduct.id);
          // Don't close the form yet - allow user to add sizes
        } else {
          throw new Error("Failed to create product");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      toast({
        title: "Error",
        description: `Failed to save product: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      });
    }
  };

  try {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? "Edit Product" : "Add New Product"}
            </DialogTitle>
            <DialogDescription>
              {editingProduct ? "Update product information" : "Create a new product for your catalog"}
            </DialogDescription>
          </DialogHeader>
        
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="basic">Basic Information</TabsTrigger>
            <TabsTrigger value="sizes" disabled={!createdProductId && !editingProduct}>
              Product Variants
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="basic" className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Product Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="category">Category *</Label>
              <Select value={formData.category} onValueChange={handleCategoryChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.name}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="price">Price (₹) *</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="original_price">Original Price (₹)</Label>
              <Input
                id="original_price"
                type="number"
                step="0.01"
                value={formData.original_price}
                onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="artistid">Artist</Label>
            <Select value={formData.artistid} onValueChange={(value) => {
              setFormData({ ...formData, artistid: value });
            }}>
              <SelectTrigger>
                <SelectValue placeholder="Select artist (optional)" />
              </SelectTrigger>
              <SelectContent>
                                 <SelectItem value="no-artist">No Artist</SelectItem>
                {artistDetails.map((artist) => (
                  <SelectItem key={artist.id} value={artist.artistid}>
                    {artist.full_name} ({artist.artistid})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formData.artistid && formData.artistid !== "no-artist" && (
              <div className="mt-2 text-sm text-gray-600">
                Selected: {artistDetails.find(a => a.artistid === formData.artistid)?.full_name}
              </div>
            )}
          </div>
          
          <div>
            <Label htmlFor="tags">Tags (comma-separated)</Label>
            <Input
              id="tags"
              value={formData.tags.join(', ')}
              onChange={(e) => {
                const tagsArray = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag !== '');
                setFormData({ ...formData, tags: tagsArray });
              }}
              placeholder="tag1, tag2, tag3"
            />
            <div className="mt-1 text-xs text-gray-500">
              Enter tags separated by commas
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="rating">Rating (0-5) *</Label>
              <Input
                id="rating"
                type="number"
                min="0"
                max="5"
                step="0.1"
                value={formData.rating}
                onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                placeholder="4.5"
              />
            </div>
            <div>
              <Label htmlFor="reviews_count">Number of Reviews</Label>
              <Input
                id="reviews_count"
                type="number"
                min="0"
                value={formData.reviews_count}
                onChange={(e) => setFormData({ ...formData, reviews_count: e.target.value })}
                placeholder="12"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="main_image">Main Image URL</Label>
              <Input
                id="main_image"
                type="url"
                value={formData.main_image}
                onChange={(e) => setFormData({ ...formData, main_image: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="in_stock"
                checked={formData.in_stock}
                onChange={(e) => setFormData({ ...formData, in_stock: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="in_stock">In Stock</Label>
            </div>
          </div>
          
          <div>
            <Label className="text-base font-medium">Additional Images (Max 4)</Label>
            <div className="space-y-3 mt-2">
              {imageInputs.map((_, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <div className="flex-1">
                    <Input
                      type="url"
                      placeholder={`Additional image ${index + 1} URL`}
                      value={imageInputValues[index] || ""}
                      onChange={(e) => handleImageInputChange(index, e.target.value)}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeImageInput(index)}
                    className="px-3"
                  >
                    Remove
                  </Button>
                </div>
              ))}
              {imageInputs.length < 4 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={addImageInput}
                  className="w-full"
                >
                  + Add Another Image
                </Button>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {editingProduct ? "Update Product" : "Create Product"}
            </Button>
          </DialogFooter>
        </form>
          </TabsContent>
          
          <TabsContent value="sizes" className="space-y-4">
            {(createdProductId || editingProduct) && (
              <ProductSizeManager
                productId={createdProductId || editingProduct!.id}
                sizes={sizes}
                onCreateSize={createSize}
                onUpdateSize={updateSize}
                onDeleteSize={deleteSize}
                onReorderSizes={reorderSizes}
                loading={sizesLoading}
              />
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
    );
  } catch (error) {
    console.error("Error rendering ProductForm:", error);
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Error Loading Form</DialogTitle>
            <DialogDescription>
              There was an error loading the product form. Please try again.
            </DialogDescription>
          </DialogHeader>
          <div className="p-4">
            <p className="text-red-600 mb-4">
              Error: {error instanceof Error ? error.message : 'Unknown error'}
            </p>
            <Button onClick={onClose} className="w-full">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
}
